import { Link } from 'react-router-dom';

export default function OtSecurity() {
  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Operational Technology Security
          </div>

          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            Securing the Physical-
            <br />
            Digital Boundary
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            IT-OT convergence security for critical infrastructure, manufacturing, energy,
            and industrial environments.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#ot-frameworks"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              View OT Frameworks
            </a>
            <Link
              to="/assessment"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              Request OT Assessment
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="purdue">
        <div className="container-ivalue">
          <div className="glass-card p-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Purdue model visualization
            </div>
            <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.02em] text-text-primary md:text-3xl">
              OT network levels
            </h2>

            <div className="mt-6 space-y-3">
              {[
                {
                  level: "Level 5",
                  title: "Enterprise Network",
                  desc: "Corporate IT, business apps, internet, cloud services",
                  tech: "Standard IT Security Stack",
                },
                {
                  level: "Level 4",
                  title: "Site Business Planning & Logistics",
                  desc: "ERP, scheduling, MES integration, BI",
                  tech: "App Security, Database Security",
                },
                {
                  level: "DMZ",
                  title: "Industrial DMZ",
                  desc: "Data diodes, jump servers, patch mgmt, historian mirror",
                  tech: "Unidirectional gateways, secure remote access",
                },
                {
                  level: "Level 3",
                  title: "Site Operations",
                  desc: "Historians, engineering workstations, HMI servers, DCs",
                  tech: "OT SIEM, asset discovery, vuln mgmt",
                },
                {
                  level: "Level 2",
                  title: "Area Supervisory Control",
                  desc: "HMI, SCADA servers, control room, alarm mgmt",
                  tech: "Network monitoring, anomaly detection",
                },
                {
                  level: "Level 1",
                  title: "Basic Control",
                  desc: "PLCs, RTUs, DCS controllers, safety systems",
                  tech: "Protocol monitoring, device hardening",
                },
                {
                  level: "Level 0",
                  title: "Process",
                  desc: "Sensors, actuators, valves, motors, physical equipment",
                  tech: "Physical security, network segmentation",
                },
              ].map((x) => (
                <div
                  key={x.level}
                  className="rounded-2xl border border-border-subtle bg-white/5 p-5"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="font-mono text-xs font-semibold text-text-secondary">
                        {x.level}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-text-primary">
                        {x.title}
                      </div>
                      <div className="mt-1 text-sm text-text-secondary">
                        {x.desc}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x.tech}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 text-xs text-text-tertiary">
              In Part 5, we can upgrade this into an interactive, click-to-expand diagram with animated flows.
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="ot-frameworks">
        <div className="container-ivalue">
          <div className="grid gap-3 lg:grid-cols-4">
            {[
              {
                t: "NIST 800-82",
                f: "Guide to ICS Security",
                c: "Controls: 50+",
                i: "Industries: All critical infrastructure",
              },
              {
                t: "NERC CIP",
                f: "Critical Infrastructure Protection",
                c: "Standards: CIP-002 through CIP-014",
                i: "Industries: Power utilities",
              },
              {
                t: "NIS2 Directive",
                f: "EU Network & Information Security",
                c: "Requirements: Risk mgmt, incident reporting",
                i: "Industries: Energy, transport, health",
              },
              {
                t: "ISA/IEC 62443",
                f: "Industrial Automation Security",
                c: "Security Levels: 1–4",
                i: "Industries: Manufacturing, process",
              },
            ].map((x) => (
              <div key={x.t} className="glass-card sheen p-5">
                <div className="text-sm font-semibold text-text-primary">{x.t}</div>
                <div className="mt-1 text-sm text-text-secondary">{x.f}</div>
                <div className="mt-4 space-y-2">
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    {x.c}
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    {x.i}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="soc">
        <div className="container-ivalue">
          <div className="glass-card p-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              IT-OT SOC integration architecture
            </div>
            <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.02em] text-text-primary md:text-3xl">
              Unified SOC dashboard
            </h2>

            <div className="mt-6 grid gap-3 lg:grid-cols-3">
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                <div className="text-sm font-semibold text-text-primary">IT SIEM/SOAR</div>
                <div className="mt-2 text-sm text-text-secondary">Splunk, Sentinel</div>
                <div className="mt-4 text-xs text-text-tertiary">
                  Normalizes identity, endpoint, network, app and cloud telemetry.
                </div>
              </div>
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                <div className="text-sm font-semibold text-text-primary">OT SIEM/SOAR</div>
                <div className="mt-2 text-sm text-text-secondary">Claroty, Nozomi</div>
                <div className="mt-4 text-xs text-text-tertiary">
                  Captures protocol-level OT telemetry and detects anomalous plant behavior.
                </div>
              </div>
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                <div className="text-sm font-semibold text-text-primary">Unified SOC Dashboard</div>
                <div className="mt-2 text-sm text-text-secondary">One triage, one reporting line</div>
                <div className="mt-4 text-xs text-text-tertiary">
                  Correlates IT+OT events for incident response and resilience testing.
                </div>
              </div>
            </div>

            <div className="mt-6 text-sm text-text-secondary">
              In Part 5, we can add an interactive architecture diagram with clickable data sources and control mappings.
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20">
        <div className="container-ivalue">
          <div className="glass-card p-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Industry applications
            </div>
            <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.02em] text-text-primary md:text-3xl">
              Where OT controls matter
            </h2>

            <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  t: "Power & Utilities",
                  f: "NERC CIP, IEC 62443",
                  k: "BES identification, Electronic Security Perimeters",
                  a: "Generation, Transmission, Distribution SCADA",
                },
                {
                  t: "Oil & Gas",
                  f: "IEC 62443",
                  k: "Safety Instrumented Systems, Pipeline SCADA",
                  a: "Refineries, Pipelines, Offshore",
                },
                {
                  t: "Manufacturing",
                  f: "IEC 62443",
                  k: "Zone segmentation, Secure remote access",
                  a: "PLCs, Robotics, MES",
                },
                {
                  t: "Transportation",
                  f: "NIS2",
                  k: "Operational continuity, Incident reporting",
                  a: "Rail control, Traffic mgmt",
                },
                {
                  t: "Water/Wastewater",
                  f: "AWWA, EPA",
                  k: "Remote access security, Chemical system protection",
                  a: "SCADA, Treatment systems",
                },
              ].map((x) => (
                <div key={x.t} className="glass-card sheen p-5">
                  <div className="text-lg font-semibold text-text-primary">{x.t}</div>
                  <div className="mt-1 text-sm text-text-secondary">Framework focus: {x.f}</div>
                  <div className="mt-4 space-y-2">
                    <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      Key controls: {x.k}
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      Critical assets: {x.a}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
